<template>
  <div
    :class="[$style.config, $style.wrap]"
    :style="(data?.fields?.cssOverride || {}) as StyleValue"
  >
    <div :class="$style.container">
      <header :class="$style.header">
        <BaseHeading v-if="data?.fields?.title" as="h2" size="h2">
          {{ data?.fields?.title }}
        </BaseHeading>
        <p v-if="computedWeekOfLabel" :class="$style.weekOf">
          {{ computedWeekOfLabel }}
        </p>
      </header>

      <div :class="$style.gridWrapper">
        <BaseButton
          v-if="!loggedIn"
          presentation="dark"
          size="default"
          :class="$style.signIn"
          @click="handleLogin"
        >
          {{ localizedStrings['sign-in-to-see-more'] || 'Sign In to See More' }}
        </BaseButton>
        <div
          :class="[$style.grid, !loggedIn && $style.masked]"
          :inert="!loggedIn"
        >
          <section :class="$style.column">
            <div :class="[$style.columnHeading, $style.actionItems]">
              <span :class="$style.accent" />
              <BaseHeading as="h3" size="h4">
                {{ data?.fields?.actionItemsLabel || 'Action Items' }}
              </BaseHeading>
            </div>

            <ul v-if="computedActionItems.length" :class="$style.list">
              <li v-for="item in computedActionItems" :key="item?.sys?.id">
                <ArticleBody
                  v-if="computedBody(item)"
                  :data="computedBody(item)"
                  :class="$style.body"
                />
              </li>
            </ul>
            <div v-if="computedActionItemsCtas.length" :class="$style.ctas">
              <CallToAction
                v-for="cta in computedActionItemsCtas"
                :key="cta.sys.id"
                :data-ctf-id="cta.sys.id"
                :cta="cta"
                :class="$style.cta"
              />
            </div>
          </section>

          <section :class="$style.column">
            <div :class="[$style.columnHeading, $style.keyDates]">
              <span :class="$style.accent" />
              <BaseHeading as="h3" size="h4">
                {{ data?.fields?.keyDatesLabel || 'Upcoming Key Dates' }}
              </BaseHeading>
            </div>

            <ul v-if="computedKeyDateRows.length" :class="$style.list">
              <li v-for="item in computedKeyDateRows" :key="item.id">
                <template v-if="item.type === 'event'">
                  <span :class="$style.date">{{ item.date }}:&nbsp;</span>
                  <span>{{ item.label }}</span>
                </template>
                <ArticleBody v-else :data="item.body" :class="$style.body" />
              </li>
            </ul>
            <div v-else-if="isLoadingFallbackKeyDates" :class="$style.loading">
              <LoaderDots />
            </div>
            <div v-if="computedKeyDatesCtas.length" :class="$style.ctas">
              <CallToAction
                v-for="cta in computedKeyDatesCtas"
                :key="cta.sys.id"
                :data-ctf-id="cta.sys.id"
                :cta="cta"
                :class="$style.cta"
              />
            </div>
            <div v-if="isDev" :class="$style.devPanel">
              <div :class="$style.devPanelHeader">
                <div>
                  <div :class="$style.devPanelEyebrow">Dev panel</div>
                  <div :class="$style.devPanelTitle">Key date state</div>
                </div>
                <div :class="$style.devPanelHint">
                  Visible data vs fallback query output
                </div>
              </div>
              <div :class="$style.devPanelGrid">
                <div :class="$style.devPanelRow">
                  <div :class="$style.devPanelRowHeader">
                    <strong>curatedKeyDates</strong>
                    <span :class="$style.devPanelCount">
                      {{ curatedKeyDates.length }}
                    </span>
                  </div>
                  <pre :class="$style.devPanelPre">{{
                    stringifyKeyDates(curatedKeyDates)
                  }}</pre>
                </div>
                <div :class="$style.devPanelRow">
                  <div :class="$style.devPanelRowHeader">
                    <strong>fallbackKeyDates</strong>
                    <span :class="$style.devPanelCount">
                      {{ (fallbackKeyDates || []).length }}
                    </span>
                  </div>
                  <pre :class="$style.devPanelPre">{{
                    stringifyKeyDates(fallbackKeyDates || [])
                  }}</pre>
                </div>
                <div :class="$style.devPanelRow">
                  <div :class="$style.devPanelRowHeader">
                    <strong>computedKeyDates</strong>
                    <span :class="$style.devPanelCount">
                      {{ computedKeyDates.length }}
                    </span>
                  </div>
                  <pre :class="$style.devPanelPre">{{
                    stringifyKeyDates(computedKeyDates)
                  }}</pre>
                </div>
                <div :class="$style.devPanelRow">
                  <div :class="$style.devPanelRowHeader">
                    <strong>computedVisibleKeyDates</strong>
                    <span :class="$style.devPanelCount">
                      {{ computedVisibleKeyDates.length }}
                    </span>
                  </div>
                  <pre :class="$style.devPanelPre">{{
                    stringifyKeyDates(computedVisibleKeyDates)
                  }}</pre>
                </div>
              </div>
            </div>
          </section>

          <section :class="$style.column">
            <div :class="[$style.columnHeading, $style.articles]">
              <span :class="$style.accent" />
              <BaseHeading as="h3" size="h4">
                {{
                  data?.fields?.articlesLabel || 'New & Recommended Articles'
                }}
              </BaseHeading>
            </div>

            <ul v-if="computedArticleRows.length" :class="$style.list">
              <li v-for="item in computedArticleRows" :key="item.id">
                <NuxtLinkLocale
                  v-if="item.type === 'article' && item.path"
                  :to="`/news/${item.path}`"
                  :class="$style.articleLink"
                >
                  {{ item.label }}
                </NuxtLinkLocale>
                <span v-else-if="item.type === 'article'">
                  {{ item.label }}
                </span>
                <ArticleBody v-else :data="item.body" :class="$style.body" />
              </li>
            </ul>
            <div v-else-if="isLoadingLatestArticles" :class="$style.loading">
              <LoaderDots />
            </div>
            <div v-if="computedArticlesCtas.length" :class="$style.ctas">
              <CallToAction
                v-for="cta in computedArticlesCtas"
                :key="cta.sys.id"
                :data-ctf-id="cta.sys.id"
                :cta="cta"
                :class="$style.cta"
              />
            </div>
            <div v-if="isDev" :class="$style.devPanel">
              <div :class="$style.devPanelHeader">
                <div>
                  <div :class="$style.devPanelEyebrow">Dev panel</div>
                  <div :class="$style.devPanelTitle">Article state</div>
                </div>
                <div :class="$style.devPanelHint">
                  Visible data vs latest query output
                </div>
              </div>
              <div :class="$style.devPanelGrid">
                <div :class="$style.devPanelRow">
                  <div :class="$style.devPanelRowHeader">
                    <strong>curatedArticles</strong>
                    <span :class="$style.devPanelCount">
                      {{ curatedArticles.length }}
                    </span>
                  </div>
                  <pre :class="$style.devPanelPre">{{
                    stringifyArticles(curatedArticles)
                  }}</pre>
                </div>
                <div :class="$style.devPanelRow">
                  <div :class="$style.devPanelRowHeader">
                    <strong>latestArticles</strong>
                    <span :class="$style.devPanelCount">
                      {{ (latestArticles || []).length }}
                    </span>
                  </div>
                  <pre :class="$style.devPanelPre">{{
                    stringifyArticles(latestArticles || [])
                  }}</pre>
                </div>
                <div :class="$style.devPanelRow">
                  <div :class="$style.devPanelRowHeader">
                    <strong>computedArticles</strong>
                    <span :class="$style.devPanelCount">
                      {{ computedArticles.length }}
                    </span>
                  </div>
                  <pre :class="$style.devPanelPre">{{
                    stringifyArticles(computedArticles)
                  }}</pre>
                </div>
                <div :class="$style.devPanelRow">
                  <div :class="$style.devPanelRowHeader">
                    <strong>computedVisibleArticles</strong>
                    <span :class="$style.devPanelCount">
                      {{ computedVisibleArticles.length }}
                    </span>
                  </div>
                  <pre :class="$style.devPanelPre">{{
                    stringifyArticles(computedVisibleArticles)
                  }}</pre>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatLocalizedDate } from '~/utils/localizeDates'
import formatAsContentfulDate from '~/utils/formatAsContentfulDate'
import { endOfWeek, startOfDay } from 'date-fns'
import type {
  TypeArticleWithoutUnresolvableLinksResponse,
  TypeArticleSkeleton,
  TypeEventWithoutUnresolvableLinksResponse,
  TypeEventSkeleton,
  TypeGatedContentWithoutUnresolvableLinksResponse,
  TypeResourceHubWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import {
  instanceOfArticle,
  instanceOfCallToAction,
  instanceOfEvent,
  instanceOfGatedContent,
  instanceOfVisiblity,
} from '~/utils/instanceOfEntry'
import type { StyleValue } from 'vue'
import { sortEventsByUrgency } from '~/services/events'

const { handleLogin } = useAuthLogin()
const { $client, $userAccessCheck, $visibilityDecision } = useNuxtApp()
const { locale } = useI18n()
const route = useRoute()
const cookieStore = useCookieStore()
const { loggedIn, highestUserLevel } = storeToRefs(useUserStore())
const isMounted = ref(false)
const isDev = computed(
  () =>
    isMounted.value &&
    (Array.isArray(route.query.resourceHubDev)
      ? route.query.resourceHubDev[0]
      : route.query.resourceHubDev) === '1',
)

const { localizedStrings } = useLocalizedStrings()

const props = defineProps<{
  data: TypeResourceHubWithoutUnresolvableLinksResponse
}>()

const computedWeekOfLabel = computed(
  () => props.data?.fields?.weekOfLabel || '',
)

const curatedKeyDates = computed(
  () =>
    props.data?.fields?.keyDates?.filter(
      (
        item,
      ): item is
        | TypeEventWithoutUnresolvableLinksResponse
        | TypeGatedContentWithoutUnresolvableLinksResponse =>
        instanceOfEvent(item) || instanceOfGatedContent(item),
    ) || [],
)

function isVisibleEntry(
  item:
    | TypeGatedContentWithoutUnresolvableLinksResponse
    | TypeArticleWithoutUnresolvableLinksResponse
    | TypeEventWithoutUnresolvableLinksResponse
    | undefined,
) {
  const visibility = item?.fields?.visibility

  if (!visibility || !instanceOfVisiblity(visibility)) return true

  const decision = $visibilityDecision(visibility)

  if (decision.visible) return true

  // Logged out: keep entries hidden only by login so the mask can obfuscate them
  if (!loggedIn.value && decision.visible === false) {
    return decision.reasons.every((reason) => reason === 'not-logged-in')
  }

  return false
}

const computedActionItems = computed(
  () =>
    props.data?.fields?.actionItems?.filter(
      (item): item is TypeGatedContentWithoutUnresolvableLinksResponse =>
        instanceOfGatedContent(item) && isVisibleEntry(item),
    ) || [],
)

const computedActionItemsCtas = computed(
  () =>
    props.data?.fields?.actionItemsCTAs?.filter(instanceOfCallToAction) || [],
)

const computedKeyDatesCtas = computed(
  () => props.data?.fields?.keyDatesCTAs?.filter(instanceOfCallToAction) || [],
)

const computedArticlesCtas = computed(
  () => props.data?.fields?.articlesCTAs?.filter(instanceOfCallToAction) || [],
)

const computedMaxArticles = computed(() => props.data?.fields?.maxArticles)

const computedVisibleCuratedKeyDates = computed(
  () => curatedKeyDates.value.filter((item) => isVisibleEntry(item)) || [],
)

const shouldFetchFallbackKeyDates = computed(
  () => computedVisibleCuratedKeyDates.value.length === 0,
)

const fallbackKeyDatesKey = useDataKey('resource-hub-fallback-key-dates')

const {
  data: fallbackKeyDates,
  refresh: refreshFallbackKeyDates,
  status: fallbackKeyDatesStatus,
} = await useAsyncData<TypeEventWithoutUnresolvableLinksResponse[]>(
  fallbackKeyDatesKey,
  async () => {
    const today = startOfDay(new Date())
    const thisWeekEnd = endOfWeek(today, { weekStartsOn: 1 })

    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
        content_type: 'event',
        locale: locale.value,
        include: 10,
        limit: 10,
        order: ['fields.startDatetime'],
        'fields.showInKeyDates': true,
        'fields.startDatetime[gte]': formatAsContentfulDate(today),
        'fields.startDatetime[lte]': formatAsContentfulDate(thisWeekEnd),
        select: [
          'sys.id',
          'sys.contentType',
          'fields.title',
          'fields.visibility',
          'fields.accessLevel',
          'fields.startDatetime',
          'fields.endDatetime',
        ],
      })

    const visibleUpcomingEvents = response.items.filter((event) => {
      const hasAccess = $userAccessCheck(
        event.fields?.accessLevel,
        highestUserLevel.value,
      )

      // Logged out: keep gated items so the mask can obfuscate them
      return (!loggedIn.value || hasAccess) && isVisibleEntry(event)
    })

    return sortEventsByUrgency(visibleUpcomingEvents)
  },
  {
    server: false,
    immediate: false,
    default: () => [],
  },
)

type ResourceHubKeyDateRow =
  | {
      id: string
      type: 'event'
      date: string
      label: string
    }
  | {
      id: string
      type: 'body'
      body: string
    }

type ResourceHubKeyDateSource =
  | TypeEventWithoutUnresolvableLinksResponse
  | TypeGatedContentWithoutUnresolvableLinksResponse

const computedKeyDates = computed<ResourceHubKeyDateSource[]>(() => {
  if (computedVisibleCuratedKeyDates.value.length) {
    return computedVisibleCuratedKeyDates.value
  }

  return fallbackKeyDates.value || []
})

const computedVisibleKeyDates = computed(
  () => computedKeyDates.value.filter((item) => isVisibleEntry(item)) || [],
)

const computedKeyDateRows = computed<ResourceHubKeyDateRow[]>(() => {
  const source: ResourceHubKeyDateSource[] = computedVisibleKeyDates.value

  return source
    .map((item): ResourceHubKeyDateRow | null => {
      if (instanceOfEvent(item)) {
        const label =
          item?.fields?.title?.toString?.() ||
          item?.fields?.entryTitle?.toString?.() ||
          ''

        return {
          id: item?.sys?.id || label || 'event',
          type: 'event',
          date: computedEventDate(item),
          label,
        }
      }

      const body = computedBody(item)
      return {
        id: item?.sys?.id || body || 'body',
        type: 'body',
        body,
      }
    })
    .filter((item) => {
      if (!item) return false

      if (item.type === 'event') {
        return Boolean(item.date || item.label)
      }

      return Boolean(item.body)
    })
    .filter((item): item is ResourceHubKeyDateRow => Boolean(item))
})

const isLoadingFallbackKeyDates = computed(
  () =>
    shouldFetchFallbackKeyDates.value &&
    ['idle', 'pending'].includes(fallbackKeyDatesStatus.value),
)

const curatedArticles = computed(
  () =>
    props.data?.fields?.articles?.filter(
      (
        item,
      ): item is
        | TypeArticleWithoutUnresolvableLinksResponse
        | TypeGatedContentWithoutUnresolvableLinksResponse =>
        instanceOfArticle(item) || instanceOfGatedContent(item),
    ) || [],
)

const computedVisibleCuratedArticles = computed(
  () => curatedArticles.value.filter((item) => isVisibleEntry(item)) || [],
)

const latestArticlesKey = useDataKey('resource-hub-latest-articles')

const {
  data: latestArticles,
  refresh: refreshLatestArticles,
  status: latestArticlesStatus,
} = await useAsyncData<TypeArticleWithoutUnresolvableLinksResponse[]>(
  latestArticlesKey,
  async () => {
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
        content_type: 'article',
        locale: locale.value,
        include: 10,
        limit: 20,
        order: ['-fields.publishedDateTime'],
        'fields.showOnNewsPage': true,
        select: [
          'sys.id',
          'sys.contentType',
          'fields.title',
          'fields.entryTitle',
          'fields.path',
          'fields.visibility',
          'fields.accessLevel',
          'fields.publishedDateTime',
        ],
      })

    return response.items
      .filter((article) => {
        const hasAccess = $userAccessCheck(
          article.fields?.accessLevel,
          highestUserLevel.value,
        )

        // Logged out: keep gated items so the mask can obfuscate them
        return (!loggedIn.value || hasAccess) && isVisibleEntry(article)
      })
      .slice(0, 3)
  },
  {
    server: false,
    immediate: false,
    default: () => [],
  },
)

const computedArticles = computed<
  Array<
    | TypeArticleWithoutUnresolvableLinksResponse
    | TypeGatedContentWithoutUnresolvableLinksResponse
  >
>(() => {
  const curatedArticleIds = new Set(
    computedVisibleCuratedArticles.value.map((article) => article.sys.id),
  )
  const uniqueLatestArticles = (latestArticles.value || []).filter(
    (article) => !curatedArticleIds.has(article.sys.id),
  )

  return [...computedVisibleCuratedArticles.value, ...uniqueLatestArticles]
})

const computedVisibleArticles = computed(() =>
  computedArticles.value.filter((item) => isVisibleEntry(item)),
)

const computedArticleRows = computed(() =>
  computedVisibleArticles.value
    .map((item) => {
      if (instanceOfArticle(item)) {
        return {
          id: item?.sys?.id || item?.fields?.path || item?.fields?.title || '',
          type: 'article' as const,
          label: item?.fields?.title || item?.fields?.entryTitle || '',
          path: item?.fields?.path || '',
        }
      }

      const body = computedBody(item)

      return {
        id: item?.sys?.id || body || '',
        type: 'body' as const,
        body,
      }
    })
    .filter((item) => {
      if (item.type === 'article') return Boolean(item.label)
      return Boolean(item.body)
    })
    .slice(
      0,
      typeof computedMaxArticles.value === 'number'
        ? computedMaxArticles.value
        : undefined,
    ),
)

const isLoadingLatestArticles = computed(() =>
  ['idle', 'pending'].includes(latestArticlesStatus.value),
)

const computedBody = (
  item:
    | TypeGatedContentWithoutUnresolvableLinksResponse
    | TypeArticleWithoutUnresolvableLinksResponse
    | TypeEventWithoutUnresolvableLinksResponse
    | undefined,
) => {
  if (!item) return ''

  const typedBody =
    (item?.fields as {
      body?: { body?: string }
      copy?: { body?: string }
    }) || {}

  return typedBody?.body?.body || typedBody?.copy?.body || ''
}

const computedEventDate = (
  event: TypeEventWithoutUnresolvableLinksResponse,
) => {
  const startDatetime = event?.fields?.startDatetime || ''
  if (!startDatetime) return ''
  return formatLocalizedDate(startDatetime, 'MMM d', locale.value)
}

const stringifyKeyDates = (
  items: Array<
    | TypeEventWithoutUnresolvableLinksResponse
    | TypeGatedContentWithoutUnresolvableLinksResponse
  >,
) =>
  JSON.stringify(
    items.map((item) => {
      const fields = item?.fields as {
        title?: string
        entryTitle?: string
        visibility?: { sys?: { id?: string } }
        startDatetime?: string
        endDatetime?: string
        width?: string
      }

      return {
        id: item?.sys?.id,
        type: item?.sys?.contentType?.sys?.id,
        title: fields?.title || fields?.entryTitle || '',
        visibility: fields?.visibility?.sys?.id || '',
        startDatetime: instanceOfEvent(item) ? fields?.startDatetime || '' : '',
        endDatetime: instanceOfEvent(item) ? fields?.endDatetime || '' : '',
        body: computedBody(item),
      }
    }),
    null,
    2,
  )

const stringifyArticles = (
  items: Array<
    | TypeArticleWithoutUnresolvableLinksResponse
    | TypeGatedContentWithoutUnresolvableLinksResponse
  >,
) =>
  JSON.stringify(
    items.map((item) => {
      const fields = item?.fields as {
        title?: string
        entryTitle?: string
        visibility?: { sys?: { id?: string } }
        publishedDateTime?: string
        path?: string
      }

      return {
        id: item?.sys?.id,
        type: item?.sys?.contentType?.sys?.id,
        title: fields?.title || fields?.entryTitle || '',
        visibility: fields?.visibility?.sys?.id || '',
        publishedDateTime: instanceOfArticle(item)
          ? fields?.publishedDateTime || ''
          : '',
        path: instanceOfArticle(item) ? fields?.path || '' : '',
        body: computedBody(item),
      }
    }),
    null,
    2,
  )

const refreshFallbackIfNeeded = () => {
  if (shouldFetchFallbackKeyDates.value) {
    refreshFallbackKeyDates()
  }
}

onMounted(refreshFallbackIfNeeded)
onMounted(() => {
  isMounted.value = true
})

watch(
  [
    shouldFetchFallbackKeyDates,
    () => locale.value,
    () => cookieStore.languageCookie,
    () => cookieStore.countryCookie,
  ],
  refreshFallbackIfNeeded,
)

const refreshLatestArticlesList = () => refreshLatestArticles()

onMounted(refreshLatestArticlesList)

watch(
  [
    () => locale.value,
    () => cookieStore.languageCookie,
    () => cookieStore.countryCookie,
    () => highestUserLevel.value,
  ],
  refreshLatestArticlesList,
)
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--resource-hub-wrap-padding',
    (
      base: 30px 20px,
      md: 30px 20px,
    )
  );

  @include breakpoint-variables(
    '--resource-hub-container-padding',
    (
      base: 28px 28px 34px,
      md: 30px 32px 36px,
    )
  );

  @include breakpoint-variables(
    '--resource-hub-grid-gap',
    (
      base: 36px,
      md: 48px,
    )
  );
}

.wrap {
  background: var(--resource-hub-wrap-background, var(--white));
  padding: var(--resource-hub-wrap-padding);
}

.container {
  max-width: var(--resource-hub-container-max-width, 1440px);
  margin: 0 auto;
  padding: var(--resource-hub-container-padding);
  background: var(--resource-hub-container-background, var(--white));
  border: var(--resource-hub-container-border, none);
  border-radius: var(--resource-hub-container-radius, 0);
}

.header {
  margin-bottom: var(--resource-hub-header-margin-bottom, 28px);

  h2 {
    font-family: var(--roboto-condensed);
    font-size: var(--resource-hub-title-font-size, clamp(2rem, 2.2vw, 3.25rem));
    line-height: var(--resource-hub-title-line-height, 1);
    margin-bottom: var(--resource-hub-title-margin-bottom, 18px);
  }
}

.weekOf {
  color: var(--resource-hub-week-of-color, var(--gray-7));
  font-size: var(--resource-hub-week-of-font-size, 18px);
  line-height: var(--resource-hub-week-of-line-height, 1.45);
}

.gridWrapper {
  position: relative;
}

.grid {
  display: grid;
  gap: var(--resource-hub-grid-gap);

  &.masked {
    filter: blur(10px);
    opacity: 0.5;
    pointer-events: none;
  }

  @include media('md') {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.column {
  min-width: 0;
  font-size: var(--resource-hub-column-font-size, 16px);
  line-height: var(--resource-hub-column-line-height, 1.45);
}

.columnHeading {
  display: flex;
  align-items: center;
  gap: var(--resource-hub-column-heading-gap, 12px);
  margin-bottom: var(--resource-hub-column-heading-margin-bottom, 14px);

  h3 {
    font-family: var(--roboto-condensed);
    font-size: var(--resource-hub-column-title-font-size, 24px);
    line-height: var(--resource-hub-column-title-line-height, 1.1);
  }
}

.accent {
  display: inline-block;
  width: var(--resource-hub-accent-width, 6px);
  min-height: var(--resource-hub-accent-height, 32px);
  border-radius: var(--resource-hub-accent-radius, 0);
  margin-top: var(--resource-hub-accent-margin-top, 2px);
  flex: 0 0 var(--resource-hub-accent-width, 6px);
  background: var(--resource-hub-action-items-accent, #504e8b);
}

.keyDates .accent {
  background: var(--resource-hub-key-dates-accent, #504e8b);
}

.articles .accent {
  background: var(--resource-hub-articles-accent, #504e8b);
}

.list {
  list-style: disc;
  padding-left: var(--resource-hub-list-padding-inline-start, 22px);
  margin: 0;

  li + li {
    margin-top: var(--resource-hub-list-item-gap, 12px);
  }
}

.body {
  display: inline;
  font-size: inherit;
  line-height: inherit;

  p {
    display: inline;
    margin: 0;
  }

  ul,
  ol {
    margin: 0;
    padding-left: 18px;
  }
}

.date {
  font-weight: 700;
}

.articleLink {
  color: #6b6aa6;
  text-decoration: underline;
}

.loading {
  padding-top: 4px;
}

.ctas {
  display: flex;
  flex-direction: column;
  align-items: var(--resource-hub-ctas-align-items, flex-start);
  gap: var(--resource-hub-ctas-gap, 12px);
  margin-top: var(--resource-hub-ctas-margin-top, 20px);
}

.cta {
  width: var(--resource-hub-cta-width, auto);
}

.devPanel {
  --resource-hub-dev-panel-background: #f8fafc;
  --resource-hub-dev-panel-border: #dbe2ea;
  --resource-hub-dev-panel-radius: 16px;
  --resource-hub-dev-panel-padding: 16px;
  --resource-hub-dev-panel-gap: 12px;
  --resource-hub-dev-panel-card-background: #ffffff;
  --resource-hub-dev-panel-card-border: #e5e7eb;
  --resource-hub-dev-panel-card-radius: 12px;
  --resource-hub-dev-panel-card-padding: 12px;
  --resource-hub-dev-panel-pre-background: #0f172a;
  --resource-hub-dev-panel-pre-color: #e2e8f0;
  --resource-hub-dev-panel-pre-radius: 10px;

  margin-top: 18px;
  padding: var(--resource-hub-dev-panel-padding);
  border: 1px solid var(--resource-hub-dev-panel-border);
  border-radius: var(--resource-hub-dev-panel-radius);
  background: var(--resource-hub-dev-panel-background);
}

.devPanelHeader {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.devPanelEyebrow {
  color: var(--gray-6);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.devPanelTitle {
  font-family: var(--roboto-condensed);
  font-size: 18px;
  line-height: 1.1;
}

.devPanelHint {
  color: var(--gray-6);
  font-size: 12px;
  line-height: 1.4;
  text-align: right;
  max-width: 190px;
}

.devPanelGrid {
  display: grid;
  gap: var(--resource-hub-dev-panel-gap);
}

.devPanelRow {
  padding: var(--resource-hub-dev-panel-card-padding);
  border: 1px solid var(--resource-hub-dev-panel-card-border);
  border-radius: var(--resource-hub-dev-panel-card-radius);
  background: var(--resource-hub-dev-panel-card-background);
  overflow: hidden;
}

.devPanelRowHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;

  strong {
    font-size: 13px;
    letter-spacing: 0.01em;
  }
}

.devPanelCount {
  font-size: 11px;
  color: var(--gray-6);
  background: #eef2f7;
  border: 1px solid #dbe2ea;
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
}

.devPanelPre {
  margin: 0;
  padding: 12px;
  border-radius: var(--resource-hub-dev-panel-pre-radius);
  background: var(--resource-hub-dev-panel-pre-background);
  color: var(--resource-hub-dev-panel-pre-color);
  font-size: 11px;
  line-height: 1.5;
  overflow: auto;
  max-height: 260px;
}

.signIn {
  z-index: 1;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 220px !important;
  max-width: 80% !important;
  font-size: to-rem(20px) !important;
}
</style>
